// src/stores/UserWallStore.ts
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './index';
import userWallService from '@/services/userWallService';
import { dummyDataService } from '@/services/dummyDataService';

export interface Post {
    id: string;
    userId: string;
    postType: 'status' | 'work_upload' | 'license_transaction' | 'achievement' | 'article';
    content: string;
    privacy: 'public' | 'followers' | 'private';
    createdAt: string;
    updatedAt: string;
    isDeleted: boolean;
    user: {
        id: string;
        username: string;
        firstName: string;
        lastName: string;
        avatar?: string;
    };
    media?: {
        id: string;
        mediaType: 'image' | 'video' | 'audio' | 'document';
        mediaUrl: string;
        thumbnailUrl?: string;
    }[];
    work?: {
        id: string;
        title: string;
        category: string;
        price: number;
        licenseType: string;
    };
    likesCount: number;
    commentsCount: number;
    sharesCount: number;
    isLiked: boolean;
    isSaved: boolean;
}

export interface UserProfile {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    bio?: string;
    avatar?: string;
    coverPhoto?: string;
    followersCount: number;
    followingCount: number;
    worksCount: number;
    licensesCount: number;
    isFollowing: boolean;
    isVerified: boolean;
    location?: string;
    website?: string;
    createdAt: string;
}

class UserWallStore {
    rootStore: RootStore;

    // Wall data
    posts: Post[] = [];
    isLoadingPosts = false;
    hasMorePosts = true;
    currentPage = 1;

    // User profile data
    userProfile: UserProfile | null = null;
    isLoadingProfile = false;

    // Interaction states
    isPostingContent = false;
    isLoadingComments = false;

    // Active tab
    activeTab: 'timeline' | 'works' | 'licenses' | 'collections' | 'about' = 'timeline';

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this);
    }

    // Generate dummy posts
    private generateDummyPosts(userId: string, count: number = 20): Post[] {
        const postTypes: Post['postType'][] = ['status', 'work_upload', 'license_transaction', 'achievement', 'article'];
        const dummyPosts: Post[] = [];

        for (let i = 0; i < count; i++) {
            const postType = postTypes[Math.floor(Math.random() * postTypes.length)];
            const createdAt = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString();

            const post: Post = {
                id: `post-${i + 1}`,
                userId,
                postType,
                content: this.generatePostContent(postType, i),
                privacy: 'public',
                createdAt,
                updatedAt: createdAt,
                isDeleted: false,
                user: {
                    id: userId,
                    username: `user${userId}`,
                    firstName: 'Nguyễn',
                    lastName: `Văn ${String.fromCharCode(65 + (parseInt(userId) % 26))}`,
                    avatar: `/src/assets/images/avatar/avatar-${(parseInt(userId) % 6) + 1}.jpg`
                },
                likesCount: Math.floor(Math.random() * 1000),
                commentsCount: Math.floor(Math.random() * 100),
                sharesCount: Math.floor(Math.random() * 50),
                isLiked: Math.random() > 0.7,
                isSaved: Math.random() > 0.8
            };

            // Add type-specific data
            if (postType === 'work_upload') {
                post.work = {
                    id: `work-${i}`,
                    title: `Tác phẩm số ${i + 1}`,
                    category: 'Thiết kế',
                    price: Math.floor(Math.random() * 5000000) + 500000,
                    licenseType: 'Standard'
                };
                post.media = [{
                    id: `media-${i}`,
                    mediaType: 'image',
                    mediaUrl: `/images/works/work-${(i % 5) + 1}.jpg`,
                    thumbnailUrl: `/images/works/thumb-${(i % 5) + 1}.jpg`
                }];
            }

            if (postType === 'license_transaction') {
                post.work = {
                    id: `work-${i}`,
                    title: `Logo Design Modern ${i}`,
                    category: 'Thiết kế đồ họa',
                    price: Math.floor(Math.random() * 2000000) + 300000,
                    licenseType: 'Commercial'
                };
            }

            if (postType === 'achievement') {
                post.content = `🎉 Đã đạt được thành tựu "${this.getRandomAchievement()}"!`;
            }

            if (['status', 'article'].includes(postType) && Math.random() > 0.7) {
                post.media = [{
                    id: `media-${i}`,
                    mediaType: 'image',
                    mediaUrl: `/images/posts/post-${(i % 10) + 1}.jpg`,
                    thumbnailUrl: `/images/posts/thumb-${(i % 10) + 1}.jpg`
                }];
            }

            dummyPosts.push(post);
        }

        return dummyPosts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }

    private generatePostContent(type: Post['postType'], index: number): string {
        const contents = {
            status: [
                'Hôm nay là một ngày tuyệt vời để sáng tạo! 🎨',
                'Vừa hoàn thành một dự án mới, cảm thấy rất hài lòng với kết quả.',
                'Chia sẻ một số kinh nghiệm về thiết kế đồ họa...',
                'Cảm ơn mọi người đã ủng hộ tác phẩm của mình!'
            ],
            work_upload: [
                'Xin giới thiệu tác phẩm mới nhất của tôi 🎨',
                'Dự án thiết kế logo vừa hoàn thành cho khách hàng',
                'Bộ sưu tập hình ảnh mới đã sẵn sàng để cấp phép'
            ],
            license_transaction: [
                'Vừa mua được giấy phép sử dụng cho một tác phẩm tuyệt vời!',
                'Cảm ơn tác giả đã tạo ra tác phẩm chất lượng',
                'Đã mua license và sẵn sàng sử dụng cho dự án mới'
            ],
            achievement: [
                'Đạt mốc 100 tác phẩm được đăng ký!',
                'Trở thành Top Seller của tháng!',
                'Nhận được chứng nhận Verified Creator'
            ],
            article: [
                'Hướng dẫn cách bảo vệ bản quyền tác phẩm số',
                '5 lời khuyên cho người mới bắt đầu trong ngành sáng tạo',
                'Xu hướng thiết kế đồ họa năm 2024'
            ]
        };

        const contentArray = contents[type];
        return contentArray[index % contentArray.length];
    }

    private getRandomAchievement(): string {
        const achievements = [
            'Top Creator của tháng',
            '1000 lượt tải xuống',
            'Verified Creator',
            '100 tác phẩm được đăng ký',
            'Best Seller 2024'
        ];
        return achievements[Math.floor(Math.random() * achievements.length)];
    }

    // Generate dummy user profile
    private generateDummyUserProfile(userId: string): UserProfile {
        const userData = dummyDataService.getUserById(userId);

        return {
            id: userId,
            username: userData.username,
            firstName: userData.fullName.split(' ')[0],
            lastName: userData.fullName.split(' ').slice(1).join(' '),
            email: userData.email,
            bio: 'Nhà thiết kế đồ họa chuyên nghiệp với hơn 5 năm kinh nghiệm. Chuyên về thiết kế logo, branding và UI/UX.',
            avatar: userData.avatar,
            coverPhoto: '/images/covers/cover-1.jpg',
            followersCount: Math.floor(Math.random() * 5000) + 100,
            followingCount: Math.floor(Math.random() * 500) + 50,
            worksCount: userData.contentCount || 0,
            licensesCount: userData.licenseCount || 0,
            isFollowing: Math.random() > 0.5,
            isVerified: userData.isVerified,
            location: 'Hồ Chí Minh, Việt Nam',
            website: userData.website,
            createdAt: userData.joinDate
        };
    }

    // Load user profile
    async loadUserProfile(userId: string) {
        this.isLoadingProfile = true;
        try {
            const profile = await userWallService.getUserProfile(userId);
            runInAction(() => {
                this.userProfile = profile;
                this.isLoadingProfile = false;
            });
        } catch (error) {
            console.log('API Error, using dummy profile data');
            // Use dummy data if API fails
            const dummyProfile = this.generateDummyUserProfile(userId);
            runInAction(() => {
                this.userProfile = dummyProfile;
                this.isLoadingProfile = false;
            });
        }
    }

    // Load posts for timeline
    async loadPosts(userId: string, page = 1) {
        if (this.isLoadingPosts) return;

        this.isLoadingPosts = true;
        try {
            const response = await userWallService.getUserPosts(userId, page);
            runInAction(() => {
                if (page === 1) {
                    this.posts = response.data;
                } else {
                    this.posts = [...this.posts, ...response.data];
                }
                this.hasMorePosts = response.data.length === 20;
                this.currentPage = page;
                this.isLoadingPosts = false;
            });
        } catch (error) {
            console.log('API Error, using dummy posts data');
            // Use dummy data if API fails
            const dummyPosts = this.generateDummyPosts(userId, 20);
            runInAction(() => {
                if (page === 1) {
                    this.posts = dummyPosts;
                } else {
                    // Simulate pagination
                    const newPosts = dummyPosts.map((post, index) => ({
                        ...post,
                        id: `post-${this.posts.length + index + 1}`,
                        createdAt: new Date(Date.now() - (this.posts.length + index) * 24 * 60 * 60 * 1000).toISOString()
                    }));
                    this.posts = [...this.posts, ...newPosts];
                }
                this.hasMorePosts = page < 3; // Simulate 3 pages of data
                this.currentPage = page;
                this.isLoadingPosts = false;
            });
        }
    }

    // Create new post
    async createPost(postData: {
        content: string;
        postType: Post['postType'];
        privacy: Post['privacy'];
        media?: File[];
        workId?: string;
    }) {
        this.isPostingContent = true;
        try {
            const newPost = await userWallService.createPost(postData);
            runInAction(() => {
                this.posts = [newPost, ...this.posts];
                this.isPostingContent = false;
            });
            return newPost;
        } catch (error) {
            // For demo purposes, create a dummy post
            const dummyPost: Post = {
                id: `post-new-${Date.now()}`,
                userId: this.userProfile?.id || '1',
                postType: postData.postType,
                content: postData.content,
                privacy: postData.privacy,                
                updatedAt: new Date().toISOString(),
                isDeleted: false,
                user: {
                    id: this.userProfile?.id || '1',
                    username: this.userProfile?.username || 'user1',
                    firstName: this.userProfile?.firstName || 'User',
                    lastName: this.userProfile?.lastName || 'Test',
                    avatar: this.userProfile?.avatar
                },
                likesCount: 0,
                commentsCount: 0,
                sharesCount: 0,
                isLiked: false,
                isSaved: false
            };

            runInAction(() => {
                this.posts = [dummyPost, ...this.posts];
                this.isPostingContent = false;
            });

            return dummyPost;
        }
    }

    // Like/unlike post
    async toggleLike(postId: string) {
        const post = this.posts.find(p => p.id === postId);
        if (!post) return;

        const originalLiked = post.isLiked;
        const originalCount = post.likesCount;

        // Optimistic update
        runInAction(() => {
            post.isLiked = !originalLiked;
            post.likesCount = originalLiked ? originalCount - 1 : originalCount + 1;
        });

        try {
            if (originalLiked) {
                await userWallService.unlikePost(postId);
            } else {
                await userWallService.likePost(postId);
            }
        } catch (error) {
            // Don't revert for dummy data
            console.log('Like toggle simulated for dummy data');
        }
    }

    // Add comment to post
    async addComment(postId: string, content: string) {
        try {
            const comment = await userWallService.addComment(postId, content);
            runInAction(() => {
                const post = this.posts.find(p => p.id === postId);
                if (post) {
                    post.commentsCount += 1;
                }
            });
            return comment;
        } catch (error) {
            // For dummy data, just update the count
            runInAction(() => {
                const post = this.posts.find(p => p.id === postId);
                if (post) {
                    post.commentsCount += 1;
                }
            });

            return {
                id: `comment-${Date.now()}`,
                content,
                postId,
                
            };
        }
    }

    // Follow/unfollow user
    async toggleFollow(userId: string) {
        if (!this.userProfile || this.userProfile.id !== userId) return;

        const originalFollowing = this.userProfile.isFollowing;
        const originalCount = this.userProfile.followersCount;

        // Optimistic update
        runInAction(() => {
            this.userProfile!.isFollowing = !originalFollowing;
            this.userProfile!.followersCount = originalFollowing ? originalCount - 1 : originalCount + 1;
        });

        try {
            if (originalFollowing) {
                await userWallService.unfollowUser(userId);
            } else {
                await userWallService.followUser(userId);
            }
        } catch (error) {
            // Don't revert for dummy data
            console.log('Follow toggle simulated for dummy data');
        }
    }

    // Load more posts (infinite scroll)
    async loadMorePosts(userId: string) {
        if (!this.hasMorePosts || this.isLoadingPosts) return;
        await this.loadPosts(userId, this.currentPage + 1);
    }

    // Set active tab
    setActiveTab(tab: typeof this.activeTab) {
        this.activeTab = tab;
    }

    // Reset store
    reset() {
        this.posts = [];
        this.isLoadingPosts = false;
        this.hasMorePosts = true;
        this.currentPage = 1;
        this.userProfile = null;
        this.isLoadingProfile = false;
        this.isPostingContent = false;
        this.isLoadingComments = false;
        this.activeTab = 'timeline';
    }
}

export default UserWallStore;